// src/admin/AdminDashboard.js
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  getCountFromServer,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "../firebase";

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState({
    pendingListings: 0,
    publishedListings: 0,
    pendingVerifications: 0,
    gross30d: 0,
    fee30d: 0,
    host30d: 0,
  });
  const [recentPendingListings, setRecentPendingListings] = useState([]);
  const [recentVerifications, setRecentVerifications] = useState([]);

  useEffect(() => {
    let alive = true;
    async function run() {
      setLoading(true);
      try {
        /** -------- Listings counts & recent pending -------- */
        const listingsCol = collection(db, "listings");
        const pendingQ = query(listingsCol, where("status", "==", "pending"));
        const publishedQ = query(listingsCol, where("status", "==", "published"));

        const [pendingCountSnap, publishedCountSnap] = await Promise.all([
          safeCount(pendingQ),
          safeCount(publishedQ),
        ]);

        const recentQ = query(
          listingsCol,
          where("status", "==", "pending"),
          orderBy("submittedAt", "desc"),
          limit(10)
        );
        const recentSnap = await getDocs(recentQ);
        const recentListings = recentSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        /** ---------------- Bookings last 30 days ----------------
         * (If you record createdAt, add where filter for >= now-30d)
         */
        const bookingsCol = collection(db, "bookings");
        const paidQ = query(bookingsCol, where("status", "==", "paid"));
        const paidSnap = await getDocs(paidQ);

        let gross = 0;
        let fee = 0;
        let host = 0;
        paidSnap.forEach((doc) => {
          const b = doc.data();
          const g = num(b.amountTotal);
          const f = num(b.platformFee);
          const h = num(b.hostEarnings) || (g - f);
          gross += g;
          fee += f;
          host += h;
        });

        /** ---------------- Verifications ---------------- */
        const verCol = collection(db, "verifications");

        // Fetch hosts with idVerification pending
        const idPendingQ = query(verCol, where("idVerification.status", "==", "pending"));
        const rtlPendingQ = query(verCol, where("rightToList.status", "==", "pending"));
        const [idPendSnap, rtlPendSnap] = await Promise.all([
          getDocs(idPendingQ),
          getDocs(rtlPendingQ),
        ]);

        // Unique host UIDs with any pending track
        const pendingUIDs = new Set([
          ...idPendSnap.docs.map((d) => d.id),
          ...rtlPendSnap.docs.map((d) => d.id),
        ]);

        // Latest 10 verification submissions across tracks
        const idRecentQ = query(
          verCol,
          where("idVerification.status", "==", "pending"),
          orderBy("idVerification.submittedAt", "desc"),
          limit(10)
        );
        const rtlRecentQ = query(
          verCol,
          where("rightToList.status", "==", "pending"),
          orderBy("rightToList.submittedAt", "desc"),
          limit(10)
        );
        const [idRecentSnap, rtlRecentSnap] = await Promise.all([
          getDocs(idRecentQ),
          getDocs(rtlRecentQ),
        ]);

        const combineRecent = (snapArr, track) =>
          snapArr.docs.map((d) => {
            const data = d.data() || {};
            const node = data[track] || {};
            return {
              uid: d.id,
              track,
              status: node.status || "unverified",
              submittedAt: node.submittedAt || null,
              type: data?.rightToList?.type || null,
            };
          });

        const recentV = [
          ...combineRecent(idRecentSnap, "idVerification"),
          ...combineRecent(rtlRecentSnap, "rightToList"),
        ]
          .sort((a, b) => {
            const da = tsToMillis(a.submittedAt);
            const db = tsToMillis(b.submittedAt);
            return db - da;
          })
          .filter(Boolean)
          .slice(0, 10);

        if (!alive) return;
        setKpis({
          pendingListings: pendingCountSnap,
          publishedListings: publishedCountSnap,
          pendingVerifications: pendingUIDs.size,
          gross30d: gross,
          fee30d: fee,
          host30d: host,
        });
        setRecentPendingListings(recentListings);
        setRecentVerifications(recentV);
      } finally {
        if (alive) setLoading(false);
      }
    }
    run();
    return () => (alive = false);
  }, []);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Overview</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Monitor supply quality and marketplace economics.
        </p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Kpi title="Pending listings" value={kpis.pendingListings} />
        <Kpi title="Published listings" value={kpis.publishedListings} />
        <Kpi title="Pending verifications" value={kpis.pendingVerifications} />
        <Kpi title="Gross (last 30d)" value={fmtCurrency(kpis.gross30d)} />
        <Kpi title="Our fee (last 30d)" value={fmtCurrency(kpis.fee30d)} />
      </div>

      {/* Newest verification submissions */}
      <section className="rounded-2xl border border-neutral-200 p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Newest verification submissions</h2>
          <a href="/admin/verifications" className="text-sm underline">
            Open queue
          </a>
        </div>
        {loading ? (
          <div className="animate-pulse text-sm text-neutral-500">Loading…</div>
        ) : recentVerifications.length === 0 ? (
          <div className="text-sm text-neutral-600">No verification submissions pending 🎉</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-neutral-500">
                <tr>
                  <Th>Host</Th>
                  <Th>Track</Th>
                  <Th>Status</Th>
                  <Th>Type</Th>
                  <Th>Submitted</Th>
                  <Th>Action</Th>
                </tr>
              </thead>
              <tbody>
                {recentVerifications.map((v, i) => (
                  <tr key={`${v.uid}-${v.track}-${i}`} className="border-t">
                    <Td><span className="font-mono">{v.uid}</span></Td>
                    <Td>{v.track === "idVerification" ? "Identity" : "Right to List"}</Td>
                    <Td>{v.status}</Td>
                    <Td>{v.type || "—"}</Td>
                    <Td>{fmtDate(v.submittedAt)}</Td>
                    <Td>
                      <a
                        href={`/admin/verifications?focus=${encodeURIComponent(v.uid)}`}
                        className="underline"
                      >
                        Review
                      </a>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Newest pending listings */}
      <section className="rounded-2xl border border-neutral-200 p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Newest pending listings</h2>
          <a href="/admin/listings" className="text-sm underline">
            Review all
          </a>
        </div>
        {loading ? (
          <div className="animate-pulse text-sm text-neutral-500">Loading…</div>
        ) : recentPendingListings.length === 0 ? (
          <div className="text-sm text-neutral-600">No pending listings 🎉</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-neutral-500">
                <tr>
                  <Th>Title</Th>
                  <Th>City</Th>
                  <Th>Price</Th>
                  <Th>Photos</Th>
                  <Th>Submitted</Th>
                </tr>
              </thead>
              <tbody>
                {recentPendingListings.map((l) => (
                  <tr key={l.id} className="border-t">
                    <Td>
                      <a href={`/admin/listings?focus=${l.id}`} className="underline">
                        {l.title || "—"}
                      </a>
                    </Td>
                    <Td>{[l.city, l.stateCode].filter(Boolean).join(", ") || "—"}</Td>
                    <Td>{l.dayPrice ? fmtCurrency(num(l.dayPrice)) : "—"}</Td>
                    <Td>{Array.isArray(l.photos) ? l.photos.length : 0}</Td>
                    <Td>{fmtDate(l.submittedAt)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Kpi({ title, value }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-5">
      <div className="text-xs uppercase tracking-wide text-neutral-500">{title}</div>
      <div className="mt-1 text-2xl font-semibold">{value ?? "—"}</div>
    </div>
  );
}

function Th({ children }) {
  return <th className="py-2 pr-4">{children}</th>;
}
function Td({ children }) {
  return <td className="py-2 pr-4">{children}</td>;
}

function num(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}
function fmtCurrency(n) {
  return Number.isFinite(n) ? n.toLocaleString(undefined, { style: "currency", currency: "USD" }) : "—";
}
function tsToMillis(ts) {
  try {
    if (!ts) return 0;
    if (typeof ts?.toDate === "function") return ts.toDate().getTime();
    if (typeof ts === "number") return ts;
    const d = new Date(ts);
    return d.getTime();
  } catch {
    return 0;
  }
}
function fmtDate(ts) {
  try {
    if (!ts) return "—";
    const d =
      typeof ts?.toDate === "function"
        ? ts.toDate()
        : typeof ts === "number"
        ? new Date(ts)
        : new Date(ts);
    return d.toLocaleString();
  } catch {
    return "—";
  }
}

// getCountFromServer fallback
async function safeCount(q) {
  try {
    const snap = await getCountFromServer(q);
    return snap.data().count || 0;
  } catch {
    const s = await getDocs(q);
    return s.size;
  }
}
